/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import {ColDef} from 'ag-grid-community';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {loadAssets} from '@ion/core/src/services/Connect';
import {Asset, AssetsFilters, AssetType} from '@ion/core/src/types/entities';
import {AssetSearchableProperty} from '@ion/core/src/types/services';
import {joinByObjectProperty} from '@ion/core/src/utils/ArrayUtil';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {clone} from '@ion/core/src/utils/ObjectUtil';
import {SearchField} from '@ion/dls/src/components/search/SearchField';
import {
  defaultTextFilterParams
} from '@ion/dls/src/grid/ColumnDefinitions';
import {
  resolveDefinitions
} from '@ion/dls/src/grid/ColumnResolver';
import {
  IonGrid,
  IonGridAPI
} from '@ion/dls/src/grid/IonGrid';
import {
  SortModelType
} from '@ion/dls/src/grid/IonGridUtils';
import {
  GridResult
} from '@ion/dls/src/grid/PaginationControls';
import {
  PAGE_SIZES
} from '@ion/dls/src/grid/settings';
import {DLSTheme, ViewSubGroupsProps} from '@ion/dls/src/types/types';
import {AssetDetailViewProps} from '../AssetDetailView';
import {getUniqItems} from '../ListEditorUtils';

const useStyles = makeStyles((theme: DLSTheme) => ({
  search : {
    width        : '320px',
    flexGrow     : 1,
    alignItems   : 'center',
    display      : 'inline-flex',
    height       : '66px',
    marginBottom : '20px'
  },
  endAdornment : {
    display              : 'flex',
    padding              : '0 10px',
    cursor               : 'pointer',
    '& .MuiSvgIcon-root' : {
      fontSize : 16
    }
  }
}));

export interface AssetDetailViewGridProps extends AssetDetailViewProps, ViewSubGroupsProps {
  accountId: any
  setAssetDetailedRow?: Function;
  searchPlaceholder?: string;
  assetDetailedSelectedRow?: any;
  assetTypeValue?: AssetsFilters['assetType'];
  includeCheckboxes?: boolean;
  initializeCheckboxes?: boolean;
  hideSubGroups?: boolean;
  isHideFilter?: boolean;
};

const defaultSort: SortModelType = {
  colId : 'name',
  sort  : 'asc'
};

/**
 * Grid to be rendered on the Detailed Asset popup
 */
export const AssetDetailViewGrid = ({
  assetId,
  accountId,
  assetTypeValue,
  searchPlaceholder,
  setAssetDetailedRow,
  singleRowSelection,
  toggleViewSubGroups,
  assetDetailedSelectedRow,
  externalColumnDefinitions,
  filterOn = {},
  isEditMode = false,
  viewSubGroups = false,
  hideSubGroups = false,
  isHideFilter = false,
  isLiveSelection = false,
  includeCheckboxes = false,
  initializeCheckboxes = false,
  includeFilteredAssetAssociation = false
}: AssetDetailViewGridProps, ref) => {
  const classes                               = useStyles();
  const ionGridRef                            = useRef<IonGridAPI>();
  const [gridRef, setGridRef]                 = useState({});
  const [gridItems, setGridItems]             = useState([]);
  const [gridApi, setGridApi]                 = useState(null);
  const [serachValue, setSerachValue]         = useState('');
  const [refreshRequired, setRefreshRequired] = useState(false);
  const [searchFilter, setSearchFilter]       = useState('');

  const searchFieldStyles = useCallback(() => ({
    searchFieldRoot : {
      height : '55px'
    },
    searchFieldInput : {
      fontSize : '16px'
    }
  }), []);

  useEffect(() => {
    if ((includeCheckboxes && initializeCheckboxes) || isLiveSelection) {
      gridApi?.deselectAll();
      checkBoxes(assetDetailedSelectedRow);
    }
    gridApi?.hideOverlay();
    if (gridApi && Array.isArray(gridItems) && !gridItems.length) {
      gridApi.showNoRowsOverlay();
    }
  }, [gridItems, assetDetailedSelectedRow]);

  const checkBoxes = useCallback(selectedItems => {
    const idMap = selectedItems?.reduce?.((acc, {id}) => {
      acc[id] = true;
      return acc;
    }, {});
    idMap && gridApi?.forEachNode((node) => {
      node.setSelected(!!idMap[node.data?.id]);
    });
    if (!idMap && selectedItems) {
      gridApi?.forEachNode((node) => {
        node.setSelected(node.data?.id === selectedItems.id);
      });
    }
  }, [gridApi]);

  useEffect(() => {
    searchFilter !== '' && setRefreshRequired(true);
  }, [searchFilter]);

  const getFilter = useCallback((searchFilter, filter) => {
    if (searchFilter && assetTypeValue === AssetType.Vehicle.toUpperCase()) {
      return {
        [AssetSearchableProperty.name]      : `*${searchFilter}*`,
        [AssetSearchableProperty.assetType] : '-VEHICLE'
      };
    }
    if (searchFilter && assetTypeValue === AssetType.Tool.toUpperCase()) {
      return {
        [AssetSearchableProperty.name]      : `*${searchFilter}*`,
        [AssetSearchableProperty.assetType] : '-TOOL'
      };
    }
    if (assetTypeValue === AssetType.Vehicle.toUpperCase()) {
      return {[AssetSearchableProperty.assetType]: '-VEHICLE'};
    }
    if (assetTypeValue === AssetType.Tool.toUpperCase()) {
      return {[AssetSearchableProperty.assetType]: '-TOOL'};
    }
    if (searchFilter) {
      return {[AssetSearchableProperty.name]: `*${searchFilter}*`};
    }
    return filter;
  }, [assetTypeValue]);

  /**
   * Returns a reference to the grids public api ..
   */

  const getAssets = useCallback(async function({
    page,
    pageSize,
    sort,
    callback,
    filter = {}
  }) {
    try {
      const {assets: items, totalCount} = await loadAssets({
        filterOn : {
          ...getFilter(searchFilter, filter),
          ...filterOn
        },
        includeSubGroups : viewSubGroups as boolean,
        pageSize         : pageSize,
        pageId           : page,
        sortOn           : sort,
        accountId
      });
      let assets;
      let count;
      if (isEditMode && includeFilteredAssetAssociation) {
        const filterItems = items?.filter(({id}) => id !== assetId);
        assets = clone(filterItems);
        count  = totalCount;
      } else {
        assets = clone(items);
        count = totalCount;
      }
      const results  = {items: assets, totalCount: count};
      callback(results);
      setGridItems(assets);
      return {assets, totalCount};
    } catch (err) {
      error('Error occurred retrieving detailed asset data.');
    }
  }, [
    assetId,
    getFilter,
    searchFilter,
    filterOn,
    viewSubGroups,
    accountId,
    isEditMode,
    includeFilteredAssetAssociation
  ]);

  // Provides reference to the grid's api.
  const onGridReady = useCallback(api => {
    setGridApi(api);
  }, []);

  const onGridAPI = useCallback(api => {
    setGridRef(api);
  }, []);

  const onSearchChange = useCallback((evt: ChangeEvent<{name?: string; value: string;}>) => {
    const {target:{value}} = evt;
    evt.persist();
    setSerachValue(value);
    if (value === '') {
      setSearchFilter('');
      setRefreshRequired(true);
    }
  }, []);

  const handleAssetSearch = useCallback(() => {
    if (serachValue !== '') {
      setSearchFilter(serachValue);
    } else {
      setSearchFilter('');
    }
  }, [serachValue]);

  const removeSerach = useCallback(() => {
    setSerachValue('');
    setSearchFilter('');
    setRefreshRequired(true);
  }, []);

  const handleRowClicked = useCallback((event) => {
    const rowsSelected = event?.data;
    setAssetDetailedRow(rowsSelected);
  }, [setAssetDetailedRow]);

  const onSelectionChanged = useCallback((event) => {
    const rowsSelected    = event.api.getSelectedRows();
    const selectedAssets  = [...rowsSelected, ...assetDetailedSelectedRow];
    const uniqItems       = getUniqItems(selectedAssets);
    setAssetDetailedRow(uniqItems);
  }, [setAssetDetailedRow, assetDetailedSelectedRow]);

  return (
    <>
      <div className={classes.search}>
        <SearchField placeholder={searchPlaceholder}
          overrides={searchFieldStyles}
          onChange={onSearchChange}
          optionsProps={{
            detailedDeviceGridSearch : true,
            onKeyPress               : handleAssetSearch,
            value                    : serachValue
          }}
          endAdornment={serachValue
            ? (
              <div className={classes.endAdornment}
                onClick={removeSerach}>
                <CloseIcon />
              </div>
            ) : null} />
      </div>
      <IonGrid ref={ionGridRef}
        showHeaderCheckbox={includeCheckboxes}
        checkboxesEnabled={includeCheckboxes}
        refreshRequired={refreshRequired}
        singleRowSelection={singleRowSelection}
        setRefreshRequired={setRefreshRequired}
        loadItems={getAssets}
        augmentItems={constructAssetDetailedGridRows}
        attributeMappings={attributeMappings}
        columnDefinitions={(externalColumnDefinitions || getColumnDefinitions)({isHideFilter})}
        onRowClicked={isLiveSelection ? handleRowClicked : null}
        gridOptions={isLiveSelection ? null : {onSelectionChanged}}
        toggleViewSubGroups={toggleViewSubGroups as any}
        viewSubGroups={viewSubGroups as boolean}
        pageSizes={PAGE_SIZES}
        rowMenusEnabled={false}
        defaultSort={defaultSort}
        onGridAPI={onGridAPI}
        onGridReady={onGridReady}
        showCustomPaginationBar
        paginationEnabled
        pageOffset
        omitHeader
        hideSubGroups={hideSubGroups}
        rowClassRules={{
          'ag-grid-row-grey-out' : function(rowNode) {
            return rowNode.data ? rowNode.data.asset : true;
          }
        }} />
    </>
  );
};

/**
 * Converts a collection of Assets and converts them to a list of items specific
 * for display within the Admin Devices Grid, while retaining all of the items existing
 * attributes,
 */
export const constructAssetDetailedGridRows = (assets: Asset[]): GridResult[] => {
  return assets.map(asset => {
    const {
      account,
      name,
      id,
      operators,
      externalId,
      assetType,
      assetCategory,
      lastModifiedOn,
      devices,
      year,
      make,
      model
    } = asset;
    const {title : accountTitle} = account || {};
    const {title : deviceTitle}  = devices?.[0] || {};
    return {
      ...asset,
      group          : accountTitle,
      alias          : externalId,
      device         : deviceTitle,
      type           : assetType === AssetType.Default ? '' : assetType,
      name           : name || id,
      category       : assetCategory,
      operatorsUI    : joinByObjectProperty(operators as any, 'title', true) || '',
      lastModifiedOn : formatDateTime(lastModifiedOn),
      deviceType     : assetType,
      year,
      make,
      model
    };
  }) as GridResult[];
};

const attributeMappings = {
  group            : 'account.name',
  alias            : 'externalId',
  type             : 'assetType',
  fuelTypeUI       : 'fuelType',
  createdOnUI      : 'createdOn',
  lastModifiedOnUI : 'lastModifiedOn',
  category         : 'assetCategory',
  make             : 'make',
  model            : 'model'
};

/**
 *
 * Returns column definitions for desktop admin devices grid
 */
const getColumnDefinitionsDesktop = ({isHideFilter}): ColDef[] => (
  [{
    headerName   : translate('admin-assets:grid.columns.name'),
    field        : 'name',
    sortable     : true,
    filter       : isHideFilter ? false : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate('admin-assets:grid.columns.type'),
    field      : 'assetType',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName   : translate('admin-assets:grid.columns.year'),
    field        : 'year',
    sortable     : false,
    filter       : isHideFilter ? false : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate('admin-assets:grid.columns.make'),
    field      : 'make',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  },
  {
    headerName : translate('admin-assets:grid.columns.model'),
    field      : 'model',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  },
  {
    headerName : translate('admin-assets:grid.columns.lastModifiedOn'),
    field      : 'lastModifiedOn',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }]
);
/**
 * @getColumnDefinitionsMobile
 *
 * Returns column definitions for mobile admin assets grid
 */
const getColumnDefinitionsMobile = ({isHideFilter}): ColDef[] => (
  [{
    headerName   : translate('admin-assets:grid.columns.name'),
    field        : 'name',
    sortable     : true,
    filter       : isHideFilter ? false : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate('admin-assets:grid.columns.type'),
    field      : 'assetType',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName   : translate('admin-assets:grid.columns.year'),
    field        : 'year',
    sortable     : false,
    filter       : isHideFilter ? false : 'text',
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    filterParams : defaultTextFilterParams
  },
  {
    headerName : translate('admin-assets:grid.columns.make'),
    field      : 'make',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  },
  {
    headerName : translate('admin-assets:grid.columns.model'),
    field      : 'model',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  },
  {
    headerName : translate('admin-assets:grid.columns.lastModifiedOn'),
    field      : 'lastModifiedOn',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }]
);

const getColumnDefinitions = ({isHideFilter}) => ({
  desktop : resolveDefinitions(getColumnDefinitionsDesktop({isHideFilter})),
  mobile  : resolveDefinitions(getColumnDefinitionsMobile({isHideFilter}))
});

export default memo(forwardRef(AssetDetailViewGrid));
